import type { BlockAvailabilityFormState } from './block-availability-form-state';
import { BLOCK_ACTOR_DEVICE, BLOCK_PERIOD_WEEKS } from '@/types/block-availability-api';

/**
 * Pure form validation. Mirrors the Save-enabled rules sprinkled
 * across the Flutter widget (`block_availability_widget.dart` +
 * `break_widget.dart` + `recurring_widget.dart`). Returns a
 * discriminated result so the Save button can disable AND show a
 * tooltip / inline message.
 *
 * Rules:
 *  1. An actor selected — practitioner or device (Flutter `:243-260`).
 *  2. Start + End time both required.
 *  3. End > Start. Same-hour requires End minute > Start minute
 *     (Flutter `break_widget.dart:501-505`).
 *  4. Reason non-whitespace (`break_widget.dart:140-156`).
 *  5. Recurring (when enabled):
 *      a. `repeatEvery >= 1` — Flutter's stepper enforces this.
 *      b. `endDate` set (Flutter `FieldType.DatePicker`, mandatory
 *         via `EnableSaveBlockAvailabilityEvent` chain).
 *      c. When period is Weeks: `days.length >= 1`.
 *
 *    NOTE on `period`: `setRecurringEnabled(true)` seeds period to
 *    `"Days"`, so a "period required" check could never fail.
 *    Samer review §4 — removed the dead rule rather than defer the
 *    seeding, since the seeded default is also the UX expectation.
 */

export type ValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

export function validateBlockForm(state: BlockAvailabilityFormState): ValidationResult {
  if (!state.actor) {
    // The reason is the Save button's tooltip, so it has to name what the
    // user is actually looking at — a device panel telling them to "select a
    // provider" sends them hunting for a control that isn't there
    // (PROD-2454).
    return {
      ok: false,
      reason:
        state.actorKind === BLOCK_ACTOR_DEVICE
          ? 'Select a device.'
          : 'Select a provider.',
    };
  }
  if (!state.startTime) {
    return { ok: false, reason: 'Select a start time.' };
  }
  if (!state.endTime) {
    return { ok: false, reason: 'Select an end time.' };
  }
  if (!isEndAfterStart(state.startTime, state.endTime)) {
    return { ok: false, reason: 'End time must be after start time.' };
  }
  if (!state.comment.trim()) {
    return { ok: false, reason: 'Enter a reason.' };
  }

  if (state.recurring) {
    if (!state.recurring.repeatEvery || state.recurring.repeatEvery < 1) {
      return { ok: false, reason: 'Repeat every must be at least 1.' };
    }
    if (!state.recurring.endDate) {
      return { ok: false, reason: 'Select a recurring end date.' };
    }
    if (
      state.recurring.period === BLOCK_PERIOD_WEEKS &&
      state.recurring.days.length === 0
    ) {
      return { ok: false, reason: 'Select at least one weekday.' };
    }
  }
  return { ok: true };
}

/**
 * Compare two `HH:MM` 24h strings. Returns true when `end > start`.
 * Equal times count as invalid (Flutter auto-bumps end+5 on equal
 * minutes, but we surface as an explicit error).
 */
export function isEndAfterStart(start: string, end: string): boolean {
  return toMinutes(end) > toMinutes(start);
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
